// Inline-image transport between the composer's DOM and Graph.
//
// Two directions, and both matter:
//
//   cid:  -> data:   resolveCidImages, so a contentEditable can render an
//                    image that only exists as a Graph attachment.
//   data: -> cid:    extractDataUriImages, because Outlook and most clients
//                    refuse to render data: URI images and have been seen
//                    leaking the raw <img> markup into the body. The
//                    canonical transport is a fileAttachment with
//                    isInline=true plus a cid: reference in the HTML.
//
// These used to live as inner functions of the App component. ComposeModal's
// draft autosave needs the data: -> cid: direction too, and needs cids that
// stay STABLE across saves so the same image isn't re-uploaded on every
// keystroke — hence the injectable `cidFor` factory rather than a hardcoded
// counter.

// Base64 needs a length that's a multiple of 4 as well as a legal alphabet.
// The alphabet check alone passes the literal string "undefined" (9 chars,
// all A-Za-z), which is exactly the payload that once reached Graph and got
// the whole message rejected with
//   Cannot convert the literal 'undefined' to the expected type 'Edm.Binary'
export function isBase64(s) {
  return typeof s === 'string' && s.length > 0 && s.length % 4 === 0
    && /^[A-Za-z0-9+/]+={0,2}$/.test(s);
}

// Matches <img ... src="data:image/<subtype>;base64,<bytes>">, capturing the
// prefix, the quote char, the mime subtype and the bytes.
const DATA_URI_IMG = /(<img[^>]*\bsrc\s*=\s*)(["'])data:image\/([a-zA-Z0-9+\-.]+);base64,([^"'\s]+)\2/g;

// Inline a message's cid: images as data: URIs so they render in the
// composer. An attachment with no contentBytes is SKIPPED, not interpolated:
// `${undefined}` stringifies to "undefined", which produced
// src="data:image/png;base64,undefined" and, once extractDataUriImages
// promoted it back to an attachment, a hard Graph 400. Leaving the cid:
// reference untouched is the same path an unknown cid already takes.
export function resolveCidImages(html, inline) {
  if (!html || !inline?.length) return html || '';
  // Normalise a cid the SAME way every other reader does (referencedCids
  // below, ComposeModal.patchQuoteImages, App.patchInlineQuoteWhenReady): strip
  // the angle brackets Graph often wraps a contentId in ("<image001@host>") so
  // it matches the HTML's bare `cid:image001@host`. WITHOUT this, a bracketed
  // contentId never matches and the inline image renders broken in the reply
  // quote — the recurring "image shows in the email but not on reply" bug,
  // which kept getting fixed in the OTHER cid helpers but not here.
  const norm = id => String(id || '').replace(/^<|>$/g, '').toLowerCase();
  const byCid = {};
  for (const a of inline) {
    if (a?.contentId && typeof a.contentBytes === 'string' && a.contentBytes !== '') {
      byCid[norm(a.contentId)] =
        `data:${a.contentType || 'application/octet-stream'};base64,${a.contentBytes}`;
    }
  }
  return html.replace(
    /((?:src|background)\s*=\s*["']?)cid:([^"'\s>]+)/gi,
    (match, prefix, cid) => (byCid[norm(cid)] ? `${prefix}${byCid[norm(cid)]}` : match)
  );
}

// Resolve a received email body's `cid:` image refs for the READER pane.
// Prefers a URL (served on demand, browser-cacheable) over an inlined data:
// URI: for each inline attachment with an id, `urlFor(attachmentId)` supplies
// the src; otherwise its contentBytes (if the message was pulled with the
// bytes) become a data: URI. Bracket-normalised like every other cid helper
// — a "<image1@host>" contentId matches a bare "cid:image1@host" and the
// reverse — because leaving THIS map un-normalised is exactly what rendered
// bracketed senders' inline images broken in the reader while replies were
// fine. Returns the rewritten HTML; a no-op when there are no inline images.
export function resolveCidToUrls(html, inline, urlFor) {
  if (!html || !inline?.length) return html || '';
  const norm = id => String(id || '').replace(/^<|>$/g, '').toLowerCase();
  const byCid = {};
  for (const a of inline) {
    if (!a?.contentId) continue;
    const k = norm(a.contentId);
    if (a.attachmentId && typeof urlFor === 'function') {
      byCid[k] = urlFor(a.attachmentId);
    } else if (a.contentBytes) {
      byCid[k] = `data:${a.contentType || 'application/octet-stream'};base64,${a.contentBytes}`;
    }
  }
  return html.replace(
    /((?:src|background)\s*=\s*["']?)cid:([^"'\s>]+)/gi,
    (match, prefix, cid) => (byCid[norm(cid)] ? `${prefix}${byCid[norm(cid)]}` : match)
  );
}

// Swap every `<img src="cid:…">` under `root` whose (bracket-normalised,
// lowercased) cid has an entry in `cidMap`, pointing its src at the mapped
// data: URI. Returns the number of <img>s changed. Patches attributes, not
// innerHTML, so a live caret in the composer is never disturbed. Idempotent:
// an <img> already resolved to a data: URI won't match `[src^="cid:"]` again,
// so re-running is a safe no-op. Shared by ComposeModal.patchQuoteImages and
// the reply/forward background delivery below.
export function swapCidImages(root, cidMap) {
  if (!root || !cidMap) return 0;
  let swapped = 0;
  for (const img of root.querySelectorAll('img[src^="cid:"]')) {
    const cid = img.getAttribute('src').slice(4).replace(/^<|>$/g, '').toLowerCase();
    const dataUri = cidMap[cid];
    if (dataUri) { img.setAttribute('src', dataUri); swapped++; }
  }
  return swapped;
}

// Deliver a resolved cid→dataURI map to the composer's quoted history, retrying
// until it actually lands.
//
// The composer (ComposeModal) is code-split / lazy-loaded, so the imperative
// handle that performs the swap can still be NULL at the moment the inline-image
// bytes finish downloading. A one-shot `composeRef.current?.patchQuoteImages?.()`
// would then silently no-op and the quoted email's inline images stay broken —
// the recurring "image shows in the received email but not on the reply" bug,
// which came back the instant the modal became lazy. So we poll: each tick,
// grab the handle (null until the chunk mounts) and attempt the swap; a swap of
// ≥1 means the body was mounted AND held the cids, so we're done. Bail the
// moment the draft is no longer active (user switched tabs / closed compose) so
// a slow fetch never patches the wrong draft. `sleep` is injectable so tests
// run without real timers.
export async function deliverQuoteCidMap({
  cidMap, getPatch, isActive,
  tries = 40, intervalMs = 100,
  sleep = ms => new Promise(r => setTimeout(r, ms)),
}) {
  if (!cidMap || !Object.keys(cidMap).length) return 0;
  for (let i = 0; i < tries; i++) {
    if (isActive && !isActive()) return 0;         // draft closed / swapped away
    const patch = typeof getPatch === 'function' ? getPatch() : null;
    const swapped = typeof patch === 'function' ? patch(cidMap) : 0;
    if (swapped > 0) return swapped;               // landed
    await sleep(intervalMs);
  }
  return 0;                                        // gave up (best-effort)
}

// Rewrite every data: URI image to a cid: reference, returning the new HTML
// plus the attachment descriptors for the images encountered.
//
// `cidFor(base64, mimeSubtype)` decides the cid for a given set of bytes and
// is where dedupe lives. It must return the SAME cid for the same bytes
// within one call (otherwise a logo repeated down a quoted chain becomes one
// attachment per occurrence — that was half of the "forward attached all our
// signature files" pile-up), and callers that persist across saves should
// make it stable across calls too.
//
// `extracted` only lists images the caller hasn't already been told about:
// cidFor returns { cid, isNew }, and we emit a descriptor only when isNew.
// That lets the draft autosave upload each image exactly once.
// Graph caps a single attachment at 30 MB; an inline image over this can't be
// attached, so extractDataUriImages leaves it as a data: URI rather than
// looping on a 413. Shared so the send path and the autosave agree.
export const MAX_INLINE_IMAGE_BYTES = 30 * 1024 * 1024;

// Decoded byte length of a base64 string, without allocating: 3 bytes per 4
// chars, minus padding.
export function base64Bytes(b64) {
  if (typeof b64 !== 'string' || !b64) return 0;
  const pad = b64.endsWith('==') ? 2 : b64.endsWith('=') ? 1 : 0;
  return Math.floor(b64.length * 3 / 4) - pad;
}

export function extractDataUriImages(html, cidFor, maxBytes = Infinity) {
  if (!html || html.indexOf('data:image/') === -1) {
    return { html: html || '', extracted: [] };
  }
  const extracted = [];
  const out = html.replace(DATA_URI_IMG, (match, prefix, quote, mimeSubtype, base64) => {
    // Never hand Graph bytes that aren't base64 — it rejects the entire send
    // with a 400 (Edm.Binary), losing the user's message. A payload this
    // broken can't render anywhere, so leave the <img> alone rather than
    // attaching it.
    if (!isBase64(base64)) return match;
    // An oversize inline image can't be attached (Graph caps attachments at
    // 30 MB) — converting it to cid: would 413 on every autosave forever, or
    // fail the whole send. Leave the data: URI in place: Graph stores data
    // URIs verbatim in a draft, so the composer keeps working; worst case the
    // recipient sees one image not render, instead of losing the message.
    if (base64Bytes(base64) > maxBytes) return match;
    const { cid, isNew } = cidFor(base64, mimeSubtype);
    if (isNew) {
      const ext = mimeSubtype.split('+')[0].toLowerCase();
      extracted.push({
        name:         `image-${extracted.length + 1}.${ext}`,
        contentType:  `image/${mimeSubtype}`,
        contentBytes: base64,
        contentId:    cid,
        isInline:     true,
      });
    }
    return `${prefix}${quote}cid:${cid}${quote}`;
  });
  return { html: out, extracted };
}

// Every cid: an HTML body references, lowercased. Used to decide which of a
// draft's inline attachments are still reachable — an image the user deleted
// from the body leaves its Graph attachment behind otherwise.
//
// Deliberately scans the FINAL body rather than the images we uploaded: a
// draft resumed from Outlook references cids we never uploaded and must not
// prune, and an un-hydrated cid: (bytes still loading) must survive too.
export function referencedCids(html) {
  const out = new Set();
  if (!html) return out;
  const re = /(?:src|background)\s*=\s*["']?cid:([^"'\s>]+)/gi;
  let m;
  while ((m = re.exec(html)) !== null) {
    // Some clients wrap the id in angle brackets (cid:<id@host>). Strip them so
    // this matches hydrateInlineImages' normalisation and Graph's contentId —
    // otherwise a still-referenced bracketed cid looks unreferenced and its
    // live attachment gets pruned.
    out.add(m[1].replace(/^<|>$/g, '').toLowerCase());
  }
  return out;
}

// cidFor factory for one-shot use (the send path): dedupe within this call
// only, fresh cids every time. `seed` keeps cids unique across concurrent
// composers; pass Date.now().
export function oneShotCidFactory(seed) {
  const byBytes = new Map();
  let counter = 0;
  return (base64) => {
    const existing = byBytes.get(base64);
    if (existing) return { cid: existing, isNew: false };
    counter += 1;
    const cid = `pp-inline-${seed}-${counter}@bot.pinpoint.promo`;
    byBytes.set(base64, cid);
    return { cid, isNew: true };
  };
}

// cidFor factory backed by a caller-owned Map that survives across calls (the
// draft autosave): the same bytes keep the same cid for the composer's whole
// lifetime, and `isNew` is true exactly once per image, so each is uploaded
// to the Graph draft a single time.
//
// `uploaded` is consulted, not written — the caller marks an image uploaded
// only after the POST succeeds, so a failed upload is retried on the next
// save instead of being silently skipped forever.
export function persistentCidFactory(cidByBytes, uploadedCids, seed) {
  let counter = cidByBytes.size;
  return (base64) => {
    let cid = cidByBytes.get(base64);
    if (!cid) {
      counter += 1;
      cid = `pp-inline-${seed}-${counter}@bot.pinpoint.promo`;
      cidByBytes.set(base64, cid);
    }
    return { cid, isNew: !uploadedCids.has(cid) };
  };
}
